// Pure functions — no React imports, no side effects.
import {
  BUDGET,
  FREE_TRANSFERS_PER_GW,
  PENALTY_PER_EXTRA_TRANSFER,
  POSITION_MAX,
  VALID_FORMATIONS,
  VALID_FORMATIONS_SET,
} from './types'
import type {
  AccountingSnapshot,
  AddBlockReason,
  CopyPayload,
  DraftPlayer,
  FormationResult,
  MySquadPlayerDto,
  PlayerListItem,
  PositionCounts,
  SaveSquadPayload,
  SquadDraft,
  TransferBaselineContext,
  ValidFormation,
} from './types'

// ── Position counting ─────────────────────────────────────────────────────────

export function getStarterCounts(starters: readonly DraftPlayer[]): PositionCounts {
  return {
    gk:  starters.filter(p => p.position === 'GK').length,
    def: starters.filter(p => p.position === 'DEF').length,
    mid: starters.filter(p => p.position === 'MID').length,
    fwd: starters.filter(p => p.position === 'FWD').length,
  }
}

function getAllPlayers(draft: SquadDraft): DraftPlayer[] {
  return [
    ...draft.starters,
    ...(draft.benchGk ? [draft.benchGk] : []),
    ...draft.bench,
  ]
}

export function getTotalCounts(draft: SquadDraft): PositionCounts {
  const all = getAllPlayers(draft)
  return {
    gk:  all.filter(p => p.position === 'GK').length,
    def: all.filter(p => p.position === 'DEF').length,
    mid: all.filter(p => p.position === 'MID').length,
    fwd: all.filter(p => p.position === 'FWD').length,
  }
}

// ── Formation derivation ──────────────────────────────────────────────────────

export function deriveFormation(starters: readonly DraftPlayer[]): FormationResult {
  const counts = getStarterCounts(starters)
  const key    = `${counts.def}-${counts.mid}-${counts.fwd}`
  if (VALID_FORMATIONS_SET.has(key)) {
    return { valid: true, formation: key as ValidFormation }
  }
  return { valid: false, counts }
}

// ── Reachable formations (dynamic empty-circle math) ───────────────────────────
//
// The valid tactics as numeric (DEF, MID, FWD) tuples — GK is always 1.
type FormationTuple = { def: number; mid: number; fwd: number }
const FORMATION_TUPLES: readonly FormationTuple[] = VALID_FORMATIONS.map((f) => {
  const [def, mid, fwd] = f.split('-').map((n) => parseInt(n, 10))
  return { def: def ?? 0, mid: mid ?? 0, fwd: fwd ?? 0 }
})

type OutfieldCounts = { def: number; mid: number; fwd: number }
type OutfieldPosition = 'DEF' | 'MID' | 'FWD'

const countOf = (t: FormationTuple | OutfieldCounts, pos: OutfieldPosition): number =>
  pos === 'DEF' ? t.def : pos === 'MID' ? t.mid : t.fwd

// Is there a valid formation that still accommodates AT LEAST these outfield counts?
// (i.e. the user can still complete a legal XI by only ADDING players). GK is handled
// separately — it never blocks an outfield formation.
export function isFormationReachable(counts: OutfieldCounts): boolean {
  return FORMATION_TUPLES.some(
    (t) => t.def >= counts.def && t.mid >= counts.mid && t.fwd >= counts.fwd,
  )
}

// Max starters of `position` reachable across every valid formation that still fits the
// other positions' current picks. This drives the dynamic empty circles: e.g. with 5 DEF
// picked, only the 5-x-x tactics remain, so MID caps at 4. Falls back to the already-picked
// count when nothing fits (over-picked / invalid mid-edit) so a filled slot is never hidden.
export function maxStartersForPosition(position: OutfieldPosition, counts: OutfieldCounts): number {
  const fits = FORMATION_TUPLES.filter(
    (t) => t.def >= counts.def && t.mid >= counts.mid && t.fwd >= counts.fwd,
  )
  if (fits.length === 0) return countOf(counts, position)
  return Math.max(...fits.map((t) => countOf(t, position)))
}

// ── Budget ────────────────────────────────────────────────────────────────────

export function getTotalCost(draft: SquadDraft): number {
  return getAllPlayers(draft).reduce((sum, p) => sum + p.price, 0)
}

export function getBudgetRemaining(draft: SquadDraft): number {
  // Round to cents: summing float prices drifts (e.g. a $96 squad leaves
  // 3.9999999996 instead of 4), which then blocks an affordable $4 pick and
  // makes the "Em orçamento" filter send maxPrice just under $4 — hiding every
  // $4 player. Money is in tenths, so rounding to 2 decimals kills the drift
  // without touching any legitimate value. Budget can legitimately reach 0.
  const remaining = Math.round((BUDGET - getTotalCost(draft)) * 100) / 100
  return remaining === 0 ? 0 : remaining   // normalize -0 (exact spend) → 0
}

// ── Squad membership ──────────────────────────────────────────────────────────

export function getDraftPlayerIds(draft: SquadDraft): ReadonlySet<string> {
  const ids = new Set<string>()
  for (const p of getAllPlayers(draft)) ids.add(p.playerId)
  return ids
}

export function getTotalPlayerCount(draft: SquadDraft): number {
  return draft.starters.length + (draft.benchGk ? 1 : 0) + draft.bench.length
}

// ── Add validation ────────────────────────────────────────────────────────────

export function canAddPlayer(
  draft: SquadDraft,
  player: PlayerListItem,
  isWindowOpen: boolean,
): { canAdd: true } | { canAdd: false; reason: AddBlockReason } {
  if (!isWindowOpen)            return { canAdd: false, reason: 'window_closed' }
  if (player.isEliminated)      return { canAdd: false, reason: 'player_eliminated' }
  if (!player.isAvailable)      return { canAdd: false, reason: 'player_unavailable' }

  const ids = getDraftPlayerIds(draft)
  if (ids.has(player.playerId)) return { canAdd: false, reason: 'already_in_squad' }

  if (getTotalPlayerCount(draft) >= 15) return { canAdd: false, reason: 'squad_full' }

  const totals = getTotalCounts(draft)
  const currentForPos =
    player.position === 'GK'  ? totals.gk  :
    player.position === 'DEF' ? totals.def :
    player.position === 'MID' ? totals.mid : totals.fwd

  if (currentForPos >= POSITION_MAX[player.position]) {
    return { canAdd: false, reason: 'position_limit_reached' }
  }

  if (getBudgetRemaining(draft) < player.price) {
    return { canAdd: false, reason: 'budget_exceeded' }
  }

  return { canAdd: true }
}

// ── Conversion: API → draft ───────────────────────────────────────────────────

import type { Position } from '@/types/domain'

const POSITION_ORDER = {
  GK: 0, DEF: 1, MID: 2, FWD: 3,
} as const satisfies Record<Position, number>

function playerDtoToDraft(p: {
  playerId: string; name: string; displayName: string
  position: DraftPlayer['position']; price: number
  teamCode: string; teamName: string; isEliminated: boolean
  gwPoints?: number | null
}): DraftPlayer {
  return {
    playerId:     p.playerId,
    name:         p.name,
    displayName:  p.displayName,
    position:     p.position,
    price:        p.price,
    teamCode:     p.teamCode,
    teamName:     p.teamName,
    isEliminated: p.isEliminated,
    gwPoints:     p.gwPoints ?? null,
  }
}

export function squadToDraft(result: { players: MySquadPlayerDto[] }): SquadDraft {
  const starters = result.players
    .filter(p => p.isStarter)
    .sort((a, b) => a.positionSlot - b.positionSlot)
    .map(playerDtoToDraft)

  const benchGkDto = result.players.find(p => !p.isStarter && p.position === 'GK')
  const benchGk    = benchGkDto ? playerDtoToDraft(benchGkDto) : null

  const bench = result.players
    .filter(p => !p.isStarter && p.position !== 'GK')
    .sort((a, b) => (a.benchOrder ?? 99) - (b.benchOrder ?? 99))
    .map(playerDtoToDraft)

  return { starters, benchGk, bench, isDirty: false }
}

// ── Conversion: draft → save payload ─────────────────────────────────────────

export function draftToPayload(draft: SquadDraft): SaveSquadPayload {
  if (!draft.benchGk) throw new Error('benchGk is required to save — canSave guard should prevent this')
  return {
    starterPlayerIds: draft.starters.map(p => p.playerId),
    benchGkPlayerId:  draft.benchGk.playerId,
    benchPlayerIds:   draft.bench.map(p => p.playerId),
  }
}

// ── Smart placement ───────────────────────────────────────────────────────────

// Returns updated draft after adding player — pure, does not mutate input.
// GK: fills starter GK slot first, then bench GK.
// Outfield: fills starters first (while < 11), then bench subs.
export function addPlayerToDraft(draft: SquadDraft, player: DraftPlayer): SquadDraft {
  if (player.position === 'GK') {
    const hasStarterGk = draft.starters.some(p => p.position === 'GK')
    // Only place GK in starters if there's a free slot AND no starter GK yet.
    if (!hasStarterGk && draft.starters.length < 11) {
      return {
        ...draft,
        starters: sortByPosition([player, ...draft.starters]),
        isDirty:  true,
      }
    }
    return { ...draft, benchGk: player, isDirty: true }
  }

  // Outfield: max 10 outfield starters so one slot is always reserved for the GK.
  // A pick only becomes a starter if doing so keeps a legal formation reachable — e.g. a
  // 5th MID while 3 FWD are picked has no valid tactic, so it lands on the bench instead.
  // (If the outfield bench is already full, fall back to a starter slot so we never create
  // a 4-player outfield bench.)
  const outfieldStarterCount = draft.starters.filter(p => p.position !== 'GK').length
  const counts = getStarterCounts(draft.starters)
  const afterAdd: OutfieldCounts = {
    def: counts.def + (player.position === 'DEF' ? 1 : 0),
    mid: counts.mid + (player.position === 'MID' ? 1 : 0),
    fwd: counts.fwd + (player.position === 'FWD' ? 1 : 0),
  }
  const canStart = outfieldStarterCount < 10 &&
    (isFormationReachable(afterAdd) || draft.bench.length >= 3)
  if (canStart) {
    return {
      ...draft,
      starters: sortByPosition([...draft.starters, player]),
      isDirty:  true,
    }
  }

  return { ...draft, bench: [...draft.bench, player], isDirty: true }
}

const MIN_STARTERS: Record<Position, number> = { GK: 1, DEF: 3, MID: 2, FWD: 1 }

// Move an outfield bench player into the starting XI — lets a user fill an empty pitch
// circle by dragging a bench player onto it. If the XI has a free outfield slot (possible
// after a removal), promote directly. Otherwise the XI is full, so swap the bench player
// with the LAST starter of the most over-subscribed position (never dropping any position
// below its minimum); that starter takes the vacated bench spot. Pure.
export function promoteBenchToStarters(draft: SquadDraft, benchIndex: number): SquadDraft {
  const player = draft.bench[benchIndex]
  if (player === undefined) return draft

  const outfieldStarters = draft.starters.filter(p => p.position !== 'GK').length
  const bench = [...draft.bench]

  if (outfieldStarters < 10) {
    // Only promote into a free slot if the player's position can still grow in a legal
    // tactic (e.g. don't let a MID promote when MID is already maxed for the picked DEF/FWD
    // — there is no empty MID circle then, only other-position ones). No-op otherwise.
    const counts = getStarterCounts(draft.starters)
    const afterAdd: OutfieldCounts = {
      def: counts.def + (player.position === 'DEF' ? 1 : 0),
      mid: counts.mid + (player.position === 'MID' ? 1 : 0),
      fwd: counts.fwd + (player.position === 'FWD' ? 1 : 0),
    }
    if (!isFormationReachable(afterAdd)) return draft
    bench.splice(benchIndex, 1)
    return { ...draft, starters: sortByPosition([...draft.starters, player]), bench, isDirty: true }
  }

  const demoteIdx = pickDemotableStarterIndex(draft.starters, player.position)
  if (demoteIdx === -1) return draft

  const starters = [...draft.starters]
  const demoted  = starters[demoteIdx]
  if (demoted === undefined) return draft
  starters[demoteIdx] = player
  bench[benchIndex]   = demoted
  return { ...draft, starters: sortByPosition(starters), bench, isDirty: true }
}

// Move an outfield starter down to the bench — mirrors dropping a bench player onto an
// empty pitch slot (promoteBenchToStarters), but in reverse. Used when a starter is dragged
// onto an empty outfield bench slot: the starter joins the bench (leaving an empty XI slot,
// which is valid mid-edit) instead of swapping with a non-existent bencher. Pure.
// No-op for GKs (they use the dedicated bench-GK slot) or when the bench is already full.
export function demoteStarterToBench(draft: SquadDraft, starterIndex: number, benchIndex: number): SquadDraft {
  const player = draft.starters[starterIndex]
  if (player === undefined || player.position === 'GK') return draft
  if (draft.bench.length >= 3) return draft

  const starters = draft.starters.filter((_, i) => i !== starterIndex)
  const bench    = [...draft.bench]
  bench.splice(Math.min(benchIndex, bench.length), 0, player)
  return { ...draft, starters, bench, isDirty: true }
}

// Index of the starter to demote when promoting an `addingPos` player into a full XI: the
// last starter of the outfield position with the largest surplus over its minimum, so no
// position drops below its minimum. Returns -1 when nothing can be safely demoted.
function pickDemotableStarterIndex(starters: readonly DraftPlayer[], addingPos: Position): number {
  const counts  = getStarterCounts(starters)
  const countOf = (pos: Position) =>
    pos === 'DEF' ? counts.def : pos === 'MID' ? counts.mid : pos === 'FWD' ? counts.fwd : counts.gk

  let bestPos: Position | null = null
  let bestSurplus = 0
  for (const pos of ['DEF', 'MID', 'FWD'] as const) {
    if (pos === addingPos) continue
    const surplus = countOf(pos) - MIN_STARTERS[pos]
    if (surplus > bestSurplus) { bestSurplus = surplus; bestPos = pos }
  }
  if (bestPos === null) return -1

  for (let i = starters.length - 1; i >= 0; i--) {
    if (starters[i]?.position === bestPos) return i
  }
  return -1
}

// Returns updated draft after removing a player — pure.
export function removePlayerFromDraft(draft: SquadDraft, playerId: string): SquadDraft {
  return {
    ...draft,
    starters: draft.starters.filter(p => p.playerId !== playerId),
    benchGk:  draft.benchGk?.playerId === playerId ? null : draft.benchGk,
    bench:    draft.bench.filter(p => p.playerId !== playerId),
    isDirty:  true,
  }
}

// Sort starters: GK → DEF → MID → FWD, then alphabetically within each group.
function sortByPosition(players: DraftPlayer[]): DraftPlayer[] {
  return [...players].sort((a, b) => {
    const pos = POSITION_ORDER[a.position] - POSITION_ORDER[b.position]
    return pos !== 0 ? pos : a.displayName.localeCompare(b.displayName)
  })
}

// ── Transfer accounting — mirrors Domain/Services/TransferAccountingService.cs ─
//
// Pure diff-at-commit accounting against the baseline (squad state at last lock).
// Empty baseline → pre-first-lock, unlimited free transfers.
export function computeAccounting(
  currentPlayerIds: ReadonlySet<string>,
  context:          TransferBaselineContext | undefined,
): AccountingSnapshot {
  if (!context) {
    return {
      hasBaseline:                 false,
      transferOption:              'OPTION_A',
      outgoingTotal:               0,
      eliminationCreditsUsed:      0,
      byeCreditsUsed:              0,
      freeTransfersUsed:           0,
      paidExtras:                  0,
      penaltyPoints:               0,
      freeTransfersRemaining:      FREE_TRANSFERS_PER_GW,
      eliminationCreditsRemaining: 0,
      byeCreditsRemaining:         0,
    }
  }

  const {
    transferOption,
    baselinePlayerIds,
    eliminatedBaselinePlayerIds,
    byeBaselinePlayerIds,
    remainingEliminationCredits,
    remainingByeCredits,
  } = context

  // Pre-first-lock: no baseline, no accounting.
  if (baselinePlayerIds.length === 0) {
    return {
      hasBaseline:                 false,
      transferOption,
      outgoingTotal:               0,
      eliminationCreditsUsed:      0,
      byeCreditsUsed:              0,
      freeTransfersUsed:           0,
      paidExtras:                  0,
      penaltyPoints:               0,
      freeTransfersRemaining:      transferOption === 'OPTION_B' ? -1 : FREE_TRANSFERS_PER_GW,
      eliminationCreditsRemaining: remainingEliminationCredits,
      byeCreditsRemaining:         remainingByeCredits,
    }
  }

  const removed        = baselinePlayerIds.filter(id => !currentPlayerIds.has(id))
  const outgoingTotal  = removed.length
  const eliminatedSet  = new Set(eliminatedBaselinePlayerIds)
  const byeSet         = new Set(byeBaselinePlayerIds)

  if (transferOption === 'OPTION_B') {
    return {
      hasBaseline:                 true,
      transferOption,
      outgoingTotal,
      eliminationCreditsUsed:      0,
      byeCreditsUsed:              0,
      freeTransfersUsed:           outgoingTotal,
      paidExtras:                  0,
      penaltyPoints:               0,
      freeTransfersRemaining:      -1,
      eliminationCreditsRemaining: remainingEliminationCredits,
      byeCreditsRemaining:         remainingByeCredits,
    }
  }

  // Option A — consume credits first, then free transfers, rest is paid.
  const eligibleForElim       = removed.filter(id => eliminatedSet.has(id)).length
  const eliminationCreditsUsed = Math.min(eligibleForElim, remainingEliminationCredits)

  const eligibleForBye = removed.filter(id => byeSet.has(id) && !eliminatedSet.has(id)).length
  const byeCreditsUsed = Math.min(eligibleForBye, remainingByeCredits)

  const normalOutgoing     = outgoingTotal - eliminationCreditsUsed - byeCreditsUsed
  const freeTransfersUsed  = Math.min(normalOutgoing, FREE_TRANSFERS_PER_GW)
  const paidExtras         = Math.max(0, normalOutgoing - FREE_TRANSFERS_PER_GW)
  const penaltyPoints      = paidExtras * PENALTY_PER_EXTRA_TRANSFER

  return {
    hasBaseline:                 true,
    transferOption,
    outgoingTotal,
    eliminationCreditsUsed,
    byeCreditsUsed,
    freeTransfersUsed,
    paidExtras,
    penaltyPoints,
    freeTransfersRemaining:      Math.max(0, FREE_TRANSFERS_PER_GW - freeTransfersUsed),
    eliminationCreditsRemaining: remainingEliminationCredits - eliminationCreditsUsed,
    byeCreditsRemaining:         remainingByeCredits - byeCreditsUsed,
  }
}

// ── Copy payload → draft ─────────────────────────────────────────────────────
//
// Stages a CopyPayload (from /v1/squads/{id}/copy-preview) into a fresh SquadDraft.
// The payload only carries playerId/displayName/position/price — `teamCode`, `teamName`
// hydrate when the user opens a player or saves and the my-squad query re-fetches.
// `isDirty` is set so the standard save flow runs the save-time penalty confirmation.
export function copyPayloadToDraft(payload: CopyPayload): SquadDraft {
  const fillCopyPlayer = (p: { playerId: string; displayName: string; position: DraftPlayer['position']; price: number }): DraftPlayer => ({
    playerId:     p.playerId,
    name:         p.displayName,
    displayName:  p.displayName,
    position:     p.position,
    price:        p.price,
    teamCode:     '',
    teamName:     '',
    isEliminated: false,
    gwPoints:     null,
  })

  const starters = [...payload.starters]
    .sort((a, b) => a.positionSlot - b.positionSlot)
    .map(fillCopyPlayer)

  const benchGk = fillCopyPlayer(payload.benchGk)

  const bench = [...payload.benchOutfield]
    .sort((a, b) => a.benchOrder - b.benchOrder)
    .map(fillCopyPlayer)

  return { starters, benchGk, bench, isDirty: true }
}

export function playerListItemToDraft(item: PlayerListItem): DraftPlayer {
  return {
    playerId:     item.playerId,
    name:         item.name,
    displayName:  item.displayName,
    position:     item.position,
    price:        item.price,
    teamCode:     item.teamCode,
    teamName:     item.teamName,
    isEliminated: item.isEliminated,
    gwPoints:     null,
  }
}
